import React from 'react';
import { Box, Typography } from '@mui/material';
import { Check, X, Layers } from 'lucide-react';
import GlassPaper from '../../GlassPaper';

function fmtUptime(sec) {
    if (!sec || !Number.isFinite(sec)) return '—';
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor(sec % 60);
    if (d > 0) return `${d} days ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function usageTone(pct, warn, crit) {
    if (pct == null) return 'muted';
    if (pct >= crit) return 'crit';
    if (pct >= warn) return 'warn';
    return 'good';
}

const TONE_COLORS = {
    good: { bar: 'rgba(59,130,246,0.35)', text: '#93c5fd' },
    warn: { bar: 'rgba(251,146,60,0.45)', text: '#fdba74' },
    crit: { bar: 'rgba(239,68,68,0.55)', text: '#fca5a5' },
    muted: { bar: 'rgba(100,116,139,0.35)', text: '#94a3b8' },
};

export default function ProxmoxNodesTable({ nodes = [] }) {
    const rows = [...nodes].sort((a, b) => {
        if (a.id != null && b.id != null) return a.id - b.id;
        return a.node.localeCompare(b.node);
    });

    return (
        <GlassPaper sx={{
            p: 0,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, rgba(30,41,59,0.55), rgba(15,23,42,0.65))',
            border: '1px solid rgba(148,163,184,0.16)',
        }}>
            <Box sx={{
                px: 1.75, py: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: '1px solid rgba(148,163,184,0.14)',
                background: 'rgba(15,23,42,0.4)',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Layers size={14} style={{ opacity: 0.8 }} />
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, letterSpacing: 0.3 }}>Nodes</Typography>
                </Box>
                <Typography sx={{ fontSize: 11, opacity: 0.55, fontVariantNumeric: 'tabular-nums' }}>
                    {rows.length} node{rows.length === 1 ? '' : 's'}
                </Typography>
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
                <Box
                    component="table"
                    sx={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 11.5,
                        '& th, & td': {
                            px: 1, py: 0.65,
                            textAlign: 'left',
                            borderBottom: '1px solid rgba(148,163,184,0.08)',
                            whiteSpace: 'nowrap',
                        },
                        '& th': {
                            fontSize: 10.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            color: 'rgba(226,232,240,0.7)',
                            background: 'rgba(15,23,42,0.35)',
                            borderBottom: '1px solid rgba(148,163,184,0.14)',
                        },
                        '& tbody tr:hover': {
                            background: 'rgba(59,130,246,0.06)',
                        },
                    }}
                >
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>ID</th>
                            <th>Online</th>
                            <th>Support</th>
                            <th>Server Address</th>
                            <th style={{ width: 110 }}>CPU usage</th>
                            <th style={{ width: 110 }}>Memory usage</th>
                            <th>Uptime</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((n) => (
                            <tr key={n.node}>
                                <td style={{ fontWeight: 600, color: '#e2e8f0' }}>{n.node}</td>
                                <td style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.85 }}>{n.id ?? '—'}</td>
                                <td>
                                    {n.online
                                        ? <Check size={14} style={{ color: '#34d399' }} />
                                        : <X size={14} style={{ color: '#f87171' }} />}
                                </td>
                                <td style={{ opacity: 0.55 }}>{n.level || '—'}</td>
                                <td style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.85 }}>{n.ip || '—'}</td>
                                <td><UsageBar pct={n.cpuPct} warn={70} crit={90} /></td>
                                <td><UsageBar pct={n.memPct} warn={65} crit={85} /></td>
                                <td style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.75 }}>{fmtUptime(n.uptimeSec)}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={8} style={{ textAlign: 'center', padding: 16, opacity: 0.55 }}>
                                    No nodes
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Box>
            </Box>
        </GlassPaper>
    );
}

function UsageBar({ pct, warn, crit }) {
    const has = pct != null && Number.isFinite(pct);
    const v = has ? Math.max(0, Math.min(100, pct)) : 0;
    const tone = usageTone(has ? pct : null, warn, crit);
    const c = TONE_COLORS[tone];
    return (
        <Box sx={{
            position: 'relative',
            height: 16,
            borderRadius: 0.75,
            background: 'rgba(15,23,42,0.55)',
            border: '1px solid rgba(148,163,184,0.12)',
            overflow: 'hidden',
        }}>
            <Box sx={{
                position: 'absolute',
                inset: 0,
                width: `${v}%`,
                background: c.bar,
                transition: 'width 500ms cubic-bezier(.4,0,.2,1)',
            }} />
            <Box sx={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10.5,
                fontWeight: 700,
                color: c.text,
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: 0.2,
            }}>
                {has ? `${Math.round(v)}%` : '—'}
            </Box>
        </Box>
    );
}
